'use client'

import React, { useState, useRef, useEffect } from 'react'

interface DropdownProps {
  options: string[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  buttonClassName?: string
  menuClassName?: string
  itemClassName?: string
  label?: string
  id?: string
}

export function Dropdown({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className = '',
  buttonClassName = '',
  menuClassName = '',
  itemClassName = '',
  label,
  id,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (option: string) => {
    onChange(option)
    setIsOpen(false)
  }

  return (
    <div data-component-name="Dropdown" className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label
          htmlFor={id}
          className="whitespace-nowrap"
          style={{
            fontSize: '15px',
            fontFamily: 'var(--font-sulsans-light)',
            color: 'rgb(7, 28, 38)',
            marginRight: '10px',
          }}
        >
          {label}
        </label>
      )}

      <button
        id={id}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`text-left flex items-center justify-between relative ${buttonClassName}`}
        style={{
          padding: '10px 30px 10px 10px',
          fontSize: '15px',
          fontFamily: 'var(--font-sulsans-light)',
          color: 'rgb(7, 28, 38)',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          transition: '0.3s ease-out',
          minWidth: '133px',
        }}
      >
        <span>{value || placeholder}</span>
        <svg
          className="absolute right-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{
            color: 'rgb(7, 28, 38)',
            width: '16px',
            height: '16px',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 ${menuClassName}`}
          style={{
            top: '50px',
            left: '-40px',
            width: '315px',
            backgroundColor: 'rgb(255, 255, 255)',
            border: '1px solid rgb(156, 164, 168)',
            borderRadius: '20px',
            boxShadow: 'rgba(156, 164, 168, 0.25) 2px 4px 25px 0px',
            overflow: 'hidden',
            maxHeight: '400px',
            overflowY: 'auto',
          }}
        >
          {options.map((option, index) => (
            <button
              key={option}
              type="button"
              onClick={() => handleSelect(option)}
              className={`w-full text-left transition-all ${itemClassName}`}
              style={{
                display: 'block',
                padding: '15px 37px 15px 40px',
                fontSize: '14.66px',
                fontFamily: 'var(--font-sulsans-light)',
                color: 'rgb(7, 28, 38)',
                backgroundColor: value === option ? 'rgba(226, 211, 197, 0.25)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: '0.3s ease-out',
                borderRadius:
                  index === 0
                    ? '20px 20px 0 0'
                    : index === options.length - 1
                      ? '0 0 20px 20px'
                      : '0',
              }}
              onMouseEnter={(e) => {
                if (value !== option) {
                  e.currentTarget.style.backgroundColor = 'rgba(226, 211, 197, 0.15)'
                }
              }}
              onMouseLeave={(e) => {
                if (value !== option) {
                  e.currentTarget.style.backgroundColor = 'transparent'
                } else {
                  e.currentTarget.style.backgroundColor = 'rgba(226, 211, 197, 0.25)'
                }
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
